import React, { useEffect, useRef, useState } from "react";
import { saveTokens } from "@/utils/tokenService";
import { exchangeImpersonationCode, setImpersonation } from "@/utils/impersonation";

/**
 * Landing page for a super admin's "view as this counselor" link
 * (/impersonate?code=…). Exchanges the one-time code for a short-lived token,
 * stores it the same way a normal login does, then reloads into the app.
 */
export const ImpersonatePage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  // The code is single-use: guard against React strict-mode double effects.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = new URLSearchParams(window.location.search).get("code");
    if (!code) {
      setError("This impersonation link is missing its code.");
      return;
    }

    (async () => {
      try {
        const data = await exchangeImpersonationCode(code);

        // No refresh token is issued; the empty one makes any refresh attempt fail fast.
        saveTokens({ accessToken: data.accessToken, refreshToken: "" });
        // After saveTokens, which clears any stale marker from a previous session.
        setImpersonation({
          sessionId: data.sessionId,
          targetName: data.user?.name ?? null,
          actorName: data.impersonatedBy?.name ?? null,
          expiresAt: data.expiresAt,
        });

        // Full reload (and drops the code from the URL/history) so the app
        // boots from the freshly stored session.
        window.location.replace("/");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not start impersonation.");
      }
    })();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      {error ? (
        <div className="max-w-sm rounded-lg border border-red-200 bg-white p-6 text-center shadow-sm">
          <p className="font-semibold text-gray-900">Impersonation failed</p>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <p className="mt-3 text-xs text-gray-400">
            Links work once and expire after about a minute. Start again from the admin portal.
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Starting impersonated session…</p>
      )}
    </div>
  );
};
